// Sky arena: dusk gradient sky, scattered floating cloud blocks, a few
// floating "islands" (low-poly rock shapes) for combat geography.

import * as THREE from 'three';

export class SkyArena {
  root = new THREE.Group();
  cloudWalls: THREE.Mesh[] = [];

  constructor() {
    // Dusk gradient sky as a giant inverted sphere.
    const skyGeom = new THREE.SphereGeometry(900, 32, 16);
    const skyMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        topColor:    { value: new THREE.Color(0x142142) },
        midColor:    { value: new THREE.Color(0xc25e3a) },
        bottomColor: { value: new THREE.Color(0xeec18a) },
      },
      vertexShader: `
        varying vec3 vWorldPos;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 midColor;
        uniform vec3 bottomColor;
        varying vec3 vWorldPos;
        void main() {
          float h = clamp((vWorldPos.y + 200.0) / 700.0, 0.0, 1.0);
          vec3 c = mix(bottomColor, midColor, smoothstep(0.0, 0.55, h));
          c = mix(c, topColor, smoothstep(0.55, 1.0, h));
          gl_FragColor = vec4(c, 1.0);
        }
      `,
    });
    this.root.add(new THREE.Mesh(skyGeom, skyMat));

    // Sun-like directional light + warm rim ambient.
    const sun = new THREE.DirectionalLight(0xffd9a0, 1.6);
    sun.position.set(80, 120, -60);
    this.root.add(sun);
    this.root.add(new THREE.AmbientLight(0x6688aa, 0.45));
    this.root.add(new THREE.HemisphereLight(0xffeecc, 0x223355, 0.5));

    // Floating low-poly rock islands, irregular but cheap.
    const rockMat = new THREE.MeshStandardMaterial({ color: 0x6a5648, roughness: 0.95, flatShading: true });
    const islandPositions = [
      new THREE.Vector3(-90, -10, 60),
      new THREE.Vector3(110, 20, -40),
      new THREE.Vector3(-30, 50, -120),
    ];
    for (const p of islandPositions) {
      const g = new THREE.IcosahedronGeometry(20 + Math.random() * 10, 0);
      // Distort verts for craggy look.
      const pos = g.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
        pos.setXYZ(i, x + (Math.random() - 0.5) * 5, y - Math.abs(Math.random() * 8), z + (Math.random() - 0.5) * 5);
      }
      g.computeVertexNormals();
      const m = new THREE.Mesh(g, rockMat);
      m.position.copy(p);
      this.root.add(m);

      // Tiny green top patch.
      const grass = new THREE.Mesh(
        new THREE.SphereGeometry(14, 8, 4, 0, Math.PI * 2, 0, Math.PI / 3.5),
        new THREE.MeshStandardMaterial({ color: 0x5a8a4d, roughness: 1.0, flatShading: true }),
      );
      grass.position.copy(p).y += 4;
      this.root.add(grass);
    }

    // Cloud walls = clusters of soft white spheres at varied heights.
    const cloudMat = new THREE.MeshStandardMaterial({
      color: 0xffffff, roughness: 1.0, transparent: true, opacity: 0.85, depthWrite: true, flatShading: true,
    });
    for (let i = 0; i < 12; i++) {
      const cluster = new THREE.Group();
      const cx = (Math.random() - 0.5) * 460;
      const cy = (Math.random() - 0.3) * 80 + 10;
      const cz = (Math.random() - 0.5) * 460;
      const blobs = 5 + Math.floor(Math.random() * 4);
      for (let j = 0; j < blobs; j++) {
        const r = 12 + Math.random() * 12;
        const b = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 1), cloudMat);
        b.position.set((Math.random() - 0.5) * 30, (Math.random() - 0.5) * 8, (Math.random() - 0.5) * 30);
        cluster.add(b);
      }
      cluster.position.set(cx, cy, cz);
      this.root.add(cluster);
    }

    // Soft horizon grid for spatial awareness when not near a feature.
    const grid = new THREE.GridHelper(800, 40, 0x3a3a55, 0x252535);
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.25;
    grid.position.y = -42;
    this.root.add(grid);
  }
}
